<!-- ============================================================
 * ContactPage.vue
 * 문의하기 페이지 — /kor/contact-us (§3)
 *
 * 구조:
 *   1. PageTitle — "문의하기" / "무엇을 도와드릴까요?" (기존 컴포넌트 재사용)
 *   2. 카드 섹션 — 초기 도입 문의(왼쪽) · 플랫폼 사용 문의(오른쪽)
 *   3. ContactInquiryDrawer — 왼쪽 슬라이드 드로어 (도입 문의)
 *   4. PlatformInquiryDrawer — 오른쪽 슬라이드 드로어 (플랫폼 사용 문의)
 *   5. 접수 완료 모달 (§3.4 — 사이트 공통 모달 UI 재사용)
 * ============================================================ -->
<template>
    <div class="cp">
        <!-- ── 1. 페이지 타이틀 (공통 컴포넌트 재사용 — 타이틀만 다국어, 기존 키 재사용) ── -->
        <PageTitle
            :main-title="this.$t('contact.section1.texts.text1-1')"
            :sub-title="this.$t('contact.section1.texts.text1-2')"
        />

        <!-- ── 2. 카드 섹션 ──────────────────────────────── -->
        <section class="c-section cp-section d-flex justify-center">
            <div class="c-contanier">
                <div class="cp-cards">
                    <!-- ── 도입 문의 카드 (왼쪽 드로어) ── -->
                    <div class="cp-card-wrap">
                        <button
                            type="button"
                            class="cp-card"
                            aria-haspopup="dialog"
                            aria-label="초기 도입 문의 폼 열기"
                            @click="openIntro"
                        >
                            <span class="cp-card__bar" aria-hidden="true" />

                            <div class="cp-card__inner">
                                <div class="cp-card__content">
                                    <span class="cp-card__kicker">Open Badge Onboarding</span>
                                    <h2 class="cp-card__title">
                                        초기 도입 문의
                                    </h2>
                                    <p class="cp-card__desc">
                                        레코스 오픈배지 도입을 검토하고 계신가요?
                                    </p>
                                    <div
                                        class="cp-card__chips"
                                        aria-hidden="true"
                                    >
                                        <span class="cp-chip">도입 상담</span>
                                        <span class="cp-chip">소개서 요청</span>
                                        <span class="cp-chip">요금 정책</span>
                                    </div>
                                </div>
                            </div>

                            <div class="cp-card__footer">
                                <span class="cp-card__arrow" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <path
                                            d="M3.5 9h11M10 4.5L14.5 9 10 13.5"
                                            stroke="currentColor"
                                            stroke-width="1.6"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </span>
                            </div>
                        </button>

                        <!-- 캐릭터 이미지 — 버튼 밖에 배치해 overflow:hidden 영향 없이 블리드 -->
                        <div class="cp-card__img-wrap" aria-hidden="true">
                            <img
                                src="@/assets/img/contact/character-intro.png"
                                alt=""
                                class="cp-card__img"
                                loading="lazy"
                            />
                        </div>
                    </div>

                    <!-- ── 플랫폼 사용 문의 카드 (오른쪽 드로어) ── -->
                    <div class="cp-card-wrap">
                        <button
                            type="button"
                            class="cp-card"
                            aria-haspopup="dialog"
                            aria-label="플랫폼 사용 문의 폼 열기"
                            @click="openPlatform"
                        >
                            <span class="cp-card__bar" aria-hidden="true" />

                            <div class="cp-card__inner">
                                <div class="cp-card__content">
                                    <span class="cp-card__kicker">Platform Support</span>
                                    <h2 class="cp-card__title">
                                        플랫폼 사용 문의
                                    </h2>
                                    <p class="cp-card__desc">
                                        레코스 플랫폼 사용 중 도움이 필요하신가요?
                                    </p>
                                    <div
                                        class="cp-card__chips"
                                        aria-hidden="true"
                                    >
                                        <span class="cp-chip">발행자 지원</span>
                                        <span class="cp-chip">수령자 지원</span>
                                        <span class="cp-chip">오류 문의</span>
                                    </div>
                                </div>
                            </div>

                            <div class="cp-card__footer">
                                <span class="cp-card__arrow" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <path
                                            d="M3.5 9h11M10 4.5L14.5 9 10 13.5"
                                            stroke="currentColor"
                                            stroke-width="1.6"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </span>
                            </div>
                        </button>

                        <div class="cp-card__img-wrap" aria-hidden="true">
                            <img
                                src="@/assets/img/contact/character-platform.png"
                                alt=""
                                class="cp-card__img"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div><!-- /cp-cards -->
            </div>
        </section>

        <!-- ── 3. 도입 문의 드로어 (왼쪽) ────────────────── -->
        <contact-inquiry-drawer
            :is-open="isIntroOpen"
            @close="closeAll"
            @submitted="handleSubmitted"
        />

        <!-- ── 4. 플랫폼 사용 문의 드로어 (오른쪽) ────────── -->
        <platform-inquiry-drawer
            :is-open="isPlatformOpen"
            @close="closeAll"
            @submitted="handleSubmitted"
        />

        <!-- ── 5. 접수 완료 모달 (§3.4 — 사이트 공통 모달 UI 재사용) ── -->
        <v-dialog
            v-model="successDialog.visible"
            width="500"
        >
            <v-card class="pa-0 rounded-lg">
                <div class="le-heading6 pa-5">
                    문의 접수 완료
                </div>

                <div
                    class="le-text-body2 pl-5 pr-6"
                    style="min-height: 88px;"
                    v-html="successMessageHtml"
                ></div>

                <div class="pa-5 d-flex justify-end gap-3">
                    <v-btn
                        color="orange1"
                        :small="$vuetify.breakpoint.smAndDown"
                        depressed
                        class="text-white1"
                        @click="closeSuccessDialog"
                    >
                        확인
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- ⚠️ 임시(배포/테스트 안내) — 종료 후 제거 (MaintenanceNotice.vue 헤더 참고) -->
        <MaintenanceNotice />
    </div>
</template>

<script>
import PageTitle              from '@/components/PageTitle.vue';
import ContactInquiryDrawer  from '@/components/inquiry/ContactInquiryDrawer.vue';
import PlatformInquiryDrawer from '@/components/inquiry/PlatformInquiryDrawer.vue';
// ⚠️ 임시(배포/테스트 안내) — 종료 후 이 import 와 아래 components 등록, 템플릿 태그를 제거
import MaintenanceNotice     from '@/components/inquiry/MaintenanceNotice.vue';

export default {
    name: 'ContactPage',

    components: {
        PageTitle,
        ContactInquiryDrawer,
        PlatformInquiryDrawer,
        MaintenanceNotice,   // ⚠️ 임시 — 종료 후 제거
    },

    data() {
        return {
            /** 도입 문의 드로어 열림 여부 */
            isIntroOpen:    false,
            /** 플랫폼 사용 문의 드로어 열림 여부 */
            isPlatformOpen: false,
            /** 접수 완료 모달 상태 */
            successDialog: {
                visible: false,
                message: '',
            },
        };
    },

    computed: {
        /**
         * 접수 완료 모달 본문. 서버 응답 메시지가 한 줄로 와도
         * 첫 문장 뒤(". ") 또는 개행(\n)에서 <br>로 줄바꿈해 가독성을 높인다.
         * 메시지는 서버/프론트가 통제하는 고정 문구라 v-html 사용해도 안전.
         */
        successMessageHtml() {
            return (this.successDialog.message || '')
                .replace(/\n/g, '<br>')
                .replace('. ', '.<br>');
        },
    },

    watch: {
        // 드로어 열림 상태에 따라 body 스크롤 방지 처리
        isIntroOpen(v)    { this._syncBodyScroll(v || this.isPlatformOpen); },
        isPlatformOpen(v) { this._syncBodyScroll(v || this.isIntroOpen);   },
    },

    beforeDestroy() {
        // 페이지 이탈 시 body 스크롤 복원
        document.body.style.overflow = '';
    },

    methods: {
        // ── 드로어 열기 ───────────────────────────────────
        openIntro()    { this.isIntroOpen    = true; },
        openPlatform() { this.isPlatformOpen = true; },

        /** 모든 드로어 닫기 */
        closeAll() {
            this.isIntroOpen    = false;
            this.isPlatformOpen = false;
        },

        // ── 제출 핸들러 ───────────────────────────────────

        /**
         * 접수 완료 처리 (§3.4)
         * 실제 API 호출·검증·오류 표시는 각 드로어가 담당하며,
         * 여기서는 성공(submitted) 시점에 드로어를 닫고 사이트 공통 모달 UI로
         * 접수 완료 안내를 표시한다.
         * @param {string} message - 서버 응답 안내 문구(도입·이용 공통)
         */
        handleSubmitted(message) {
            this.closeAll();
            this.successDialog = {
                visible: true,
                message: message || '문의가 접수되었습니다.\n빠른 시일 내 연락드리겠습니다.',
            };
        },

        /** 접수 완료 모달 닫기 */
        closeSuccessDialog() {
            this.successDialog.visible = false;
        },

        // ── 내부 헬퍼 ─────────────────────────────────────

        /** body 스크롤 방지 / 해제 */
        _syncBodyScroll(prevent) {
            document.documentElement.style.overflow = prevent ? 'hidden' : '';
            document.body.style.overflow = prevent ? 'hidden' : '';
        },
    },
};
</script>

<style lang="scss" scoped>
// ─── 디자인 토큰 ────────────────────────────────────────────
$orange    : #FF8228;
$orange-lt : rgba(255, 130, 40, .08);
$blue-dark : #0E275D;
$border    : #efdcc8;   // 카드 테두리 (기획 HTML 준수)
$card-bg   : #fffaf7;   // 카드 배경
$active-clr: #bc6418;
$card-radius: 16px;

// ─── 섹션 레이아웃 ──────────────────────────────────────────
.cp-section {
    padding-top:    72px;
    padding-bottom: 100px;

    @media (max-width: 1366px) { padding-top: 56px; padding-bottom: 80px; }
    @media (max-width: 767px)  { padding-top: 40px; padding-bottom: 56px; }
}

// ─── 카드 그리드 ────────────────────────────────────────────
.cp-cards {
    display:               grid;
    grid-template-columns: repeat(2, minmax(0, 500px));
    justify-content:       center;
    gap:                   24px;
    margin:                0 auto;

    @media (max-width: 900px) {
        grid-template-columns: minmax(0, 500px);
    }
}

// ─── 카드 래퍼 (position context for image bleed) ────────────
.cp-card-wrap {
    position: relative;
}

// ─── 카드 버튼 ──────────────────────────────────────────────
// overflow:hidden 유지 → bar가 border-radius 안에서 깔끔히 클리핑됨
// 이미지는 버튼 밖 .cp-card-wrap 기준으로 absolute 배치해 블리드
.cp-card {
    position:       relative;
    display:        flex;
    flex-direction: column;
    width:          100%;
    min-height:     280px;
    border:         1.5px solid $border;
    border-radius:  $card-radius;
    background:     $card-bg;
    box-shadow:     0 16px 40px rgba(17, 24, 39, .065);
    cursor:         pointer;
    text-align:     left;
    padding:        0;
    overflow:       hidden;
    transition:     transform .22s ease, box-shadow .22s ease, border-color .18s ease;

    &:hover {
        transform:    translateY(-6px);
        box-shadow:   0 28px 56px rgba(17, 24, 39, .11);
        border-color: #ffc89a;
    }

    &:focus-visible {
        outline:        2px solid $orange;
        outline-offset: 2px;
    }
}

// 상단 오렌지 포인트 바 — overflow:hidden이 border-radius로 코너 클리핑
.cp-card__bar {
    display:     block;
    height:      5px;
    flex-shrink: 0;
    background:  linear-gradient(90deg, $orange 0%, #FF6442 100%);
}

// 카드 내부 레이아웃
.cp-card__inner {
    padding: 28px 28px 0;
    flex:    1 1 auto;
}

.cp-card__content {
    padding-right: 180px;

    @media (max-width: 900px)  { padding-right: 160px; }
    @media (max-width: 480px)  { padding-right: 130px; }
    @media (max-width: 360px)  { padding-right: 0; }
}

// 킥커 (영문 소분류) — title의 소분류이므로 title에 바짝 붙임
.cp-card__kicker {
    display:        block;
    font-size:      1.25rem;
    font-weight:    700;
    color:          #FF6442;
    letter-spacing: .04em;
    margin-bottom:  4px;
    text-transform: uppercase;
}

// 카드 제목
// _common.scss 전역 h 태그에 color !important 가 있어 명시적으로 override
.cp-card__title {
    font-size:      3rem;
    font-weight:    800;
    color:          $blue-dark !important;
    margin:         0 0 8px;
    line-height:    1.3;
    letter-spacing: -0.03em;

    @media (max-width: 1200px) { font-size: 2.5rem; }
    @media (max-width: 767px)  { font-size: 2.1rem; }
}

// 카드 설명
.cp-card__desc {
    font-size:   1.4rem;
    color:       #8a97a8;
    line-height: 1.6;
    margin:      0 0 14px;
}

// 키워드 칩 그룹
.cp-card__chips {
    display:    flex;
    flex-wrap:  wrap;
    gap:        8px;
    margin-top: 12px;
    margin-bottom: 24px;
}

// 캐릭터 이미지 (absolute — cp-card-wrap 기준, 버튼 밖이라 overflow:hidden 영향 없음)
.cp-card__img-wrap {
    position:       absolute;
    right:          -18px;
    bottom:         -20px;
    z-index:        2;
    pointer-events: none;
}

.cp-card__img {
    display:    block;
    width:      auto;
    height:     279px;
    object-fit: contain;
    filter:     drop-shadow(0 8px 14px rgba(118, 67, 36, .12));

    @media (max-width: 900px)  { height: 234px; }
    @media (max-width: 480px)  { height: 189px; }
    @media (max-width: 360px)  { display: none; }
}

// 하단 화살표 영역
.cp-card__footer {
    display:         flex;
    justify-content: flex-start;
    align-items:     center;
    padding:         12px 28px 20px;
    flex-shrink:     0;
}

.cp-card__arrow {
    width:           36px;
    height:          36px;
    border-radius:   50%;
    background:      $orange;
    color:           #fff;
    display:         flex;
    align-items:     center;
    justify-content: center;
    transition:      transform .18s ease, background .18s ease;

    .cp-card:hover & {
        transform:  translateX(4px);
        background: #e67320;
    }
}

// ─── 키워드 칩 ──────────────────────────────────────────────
.cp-chip {
    display:       inline-flex;
    align-items:   center;
    height:        28px;
    padding:       0 12px;
    border:        1px solid #f0dbc8;
    border-radius: 99px;
    background:    rgba(255, 255, 255, .8);
    font-size:     1.2rem;
    font-weight:   600;
    color:         $active-clr;
    white-space:   nowrap;
}

</style>
